/**
 * @file WebSocket subscription contracts for `@labcraft/hypervisor`.
 *       The pty bridge (binary frames) and the gate channel (control
 *       events) each get a typed `SubscriptionContract`.
 * @module @labcraft/hypervisor/shared/subscriptions
 *
 * Paths are wire-true and must start with the daemon's `id` prefix
 * (`/hypervisor`). Binary frames on the pty WS are pty bytes (opaque,
 * unvalidated); text frames carry control envelopes (resize, resume).
 */

import { z } from 'zod'
import { defineSubscription } from '@labcraft/core'
import {
  CapacityAvailabilitySchema,
  HypervisorKindSchema,
  MachineLifecycleReasonSchema,
  MachineLifecycleStateSchema,
  MachineOwnershipSchema,
} from './routes.ts'
import { GateEventFrameSchema } from './events.ts'

/**
 * Machine lifecycle vocabulary (state + transition reason) lives in
 * `routes.ts` — the machine value-object module — so `MachineSchema`
 * can carry a lifecycle-state field without a `routes ↔ subscriptions`
 * import cycle (umbrella #1258 Part H / #1337). Re-exported here so
 * `@labcraft/hypervisor/shared/subscriptions` consumers (lab's
 * `ws_messages.ts`, admin's `hypervisor_routes.ts`, the internal
 * provider/pty modules) keep importing them from this path unchanged.
 */
export {
  MachineLifecycleReasonSchema,
  MachineLifecycleStateSchema,
  type MachineLifecycleReason,
  type MachineLifecycleState,
} from './routes.ts'

////////////////////////////////////////////////////////////////////////////////
// PTY subscription — terminal I/O
////////////////////////////////////////////////////////////////////////////////

/**
 * Machine recipe carried on the WS-open frame (#832). Enough for the
 * hypervisor to `boot()` a VM when the dialed `vmKey` (the `:id` path
 * param) has no live session yet. `kind` is the author's desired backend
 * (advisory — the deploy's registered provider serves the boot regardless,
 * see `MachineProvider.pick`). The base floor + per-VM RW top are
 * deploy-shape (always applied by the hypervisor — the base SELECTED by
 * `base`/default per #1332); `layers` carries only the author's optional
 * additional read-only overlay layers per-boot (ADR-032, umbrella #1256),
 * threaded here from the Part's `MachineSpec.layers`.
 *
 * @property kind - Author's desired backend; advisory for provider routing.
 */
export const PtyOpenRecipeSchema = z.object({
  kind: HypervisorKindSchema,
  /**
   * Optional per-Part base-layer override (#1332): the layer-id of the
   * base floor this VM stacks on. The hypervisor selects the staged base
   * whose `layer.json` id matches AND is `kind === 'base'`. Absent → the
   * platform default base. Threaded from the Part's `MachineSpec.base`.
   */
  base: z.string().min(1).optional(),
  /**
   * Optional additional read-only overlay layers above the base floor
   * (ADR-032, #1256), base-first. Threaded from the Part's
   * `MachineSpec.layers`; the hypervisor prepends its base floor
   * (selected by `layer.json` id + `kind === 'base'`, #1332) and appends
   * a per-VM RW top. Absent → the base floor alone.
   */
  layers: z.array(z.string().min(1)).nonempty().optional(),
  /**
   * Recorded ownership metadata (#1054): the semantic components of
   * the dialed `vmKey` — owner learnerId, browser machine name,
   * content spec hash. The hypervisor records them onto the session +
   * manifest so ownership is inspectable and the durable record knows
   * who a jail belongs to. Optional (the fake tier / legacy dials omit
   * it); consumed only on the boot path.
   */
  ownership: MachineOwnershipSchema.optional(),
  /**
   * Remaining running-time budget in ms (#1055). The hypervisor — the
   * lifecycle owner — arms a per-session deadline timer for this and
   * suspends the VM (emitting a `budget-exhausted` lifecycle frame)
   * when it elapses, even with zero sockets attached. `null` / absent
   * = unlimited (paid tier). Lab computes it from account's budget on
   * the WS-open gate and hands the deadline mechanism to the owner;
   * policy (tiers/ledger) stays in lab + account.
   */
  maxRunningMs: z.number().int().nonnegative().nullable().optional(),
})

/** Parsed `PtyOpenRecipe`. */
export type PtyOpenRecipe = z.infer<typeof PtyOpenRecipeSchema>

/**
 * Open frame — the FIRST client text frame on a pty WS (#832). The
 * WS-open drives the VM lifecycle: the hypervisor dispatches by the
 * dialed `vmKey`'s current state — `null` → `boot(recipe)`, `saved` →
 * implicit resume, `running` → attach-only. `vmKey` is the opaque `:id`
 * path param; the hypervisor never parses it (lab keys it as
 * `${learnerId}:${courseId}:…:${slot}`, but that's lab's business).
 * The recipe rides here rather than the path so the hypervisor can boot
 * a not-yet-existing VM from a single WS open, retiring the separate
 * `hypervisor.boot` HTTP fan-out lab used to issue.
 *
 * @property type - Literal `'open'` (discriminant).
 * @property recipe - The boot recipe; consumed only when the VM must be
 *   spawned (null state), ignored on attach-only / resume.
 */
export const PtyOpenFrameSchema = z.object({
  type: z.literal('open'),
  recipe: PtyOpenRecipeSchema,
})

/**
 * Resize frame — pty grows / shrinks to the supplied dimensions.
 * Nested `payload` so lab can forward the text frame byte-transparent
 * from the browser without re-encoding.
 *
 * @property type - Literal `'resize'` (discriminant).
 * @property payload - Cols/rows of the caller's PTY window; positive integers.
 */
export const PtyResizeFrameSchema = z.object({
  type: z.literal('resize'),
  payload: z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
  }),
})

/**
 * Resume frame — request the server replay buffered bytes after a
 * reconnect. Backend implementations decide whether to honor it.
 */
export const PtyResumeFrameSchema = z.object({
  type: z.literal('resume'),
})

/**
 * Suspend frame — voluntary Pause from the SPA. Server-side dispatches
 * to {@link MachineProvider.suspend} which, per umbrella #824
 * Phase 4, terminates the VM (state `running → terminating → saved`
 * once Phase 5's chapter-start snapshot lands; `running → terminating
 * → null` in the meantime). The SPA-facing path; the still-live
 * `POST /hypervisor/:id/suspend` HTTP route (`SuspendMachineRoute`) is
 * the operator-facing twin and dispatches to the same provider
 * `suspend(id)`.
 */
export const PtySuspendFrameSchema = z.object({
  type: z.literal('suspend'),
})

/**
 * Client → server control frames for the pty bridge.
 */
export const PtyControlFrameSchema = z.discriminatedUnion('type', [
  PtyOpenFrameSchema,
  PtyResizeFrameSchema,
  PtyResumeFrameSchema,
  PtySuspendFrameSchema,
])

/** Parsed `PtyControlFrame`. */
export type PtyControlFrame = z.infer<typeof PtyControlFrameSchema>

/**
 * Lifecycle frame — server-side push announcing a state transition
 * (or the initial state on attach). `reason` (#1056) carries the
 * transition trigger so the SPA can pick a per-machine recovery. This
 * is the hypervisor's own frame; lab forwards it verbatim (#1059) —
 * the engagement-TTL deadline the SPA needs travels on lab's separate
 * `engagement` frame, not stamped onto this one.
 *
 * @property type - Literal `'lifecycle'` (discriminant).
 * @property state - Current `MachineLifecycleState`.
 * @property reason - Transition trigger, optional (absent on the
 *   boot-path frames, present on the reclaim/terminal ones).
 */
export const PtyLifecycleFrameSchema = z.object({
  type: z.literal('lifecycle'),
  state: MachineLifecycleStateSchema,
  reason: MachineLifecycleReasonSchema.optional(),
  /**
   * Budget usage record (#1055): the running-window id + accumulated
   * running ms, stamped by the hypervisor (the lifecycle owner) on
   * frames while a session is/was `running`. Lab relays it to
   * account's idempotent ledger keyed `(vmId, windowId)`, so two tabs
   * on one VM — sharing one window — bill once. Absent on boot-path
   * frames before the window opens.
   */
  usage: z
    .object({
      windowId: z.string().min(1),
      runningMs: z.number().int().nonnegative(),
    })
    .optional(),
})

/** Parsed `PtyLifecycleFrame`. */
export type PtyLifecycleFrame = z.infer<typeof PtyLifecycleFrameSchema>

/**
 * Resize frame (server → client) — announces the pty's current window
 * size to every attached consumer. Fired when any consumer resizes the
 * guest pty (broadcast to the *other* attached conns, never echoed to
 * the originator) and once on attach so a late-joining observer renders
 * at the current dimensions immediately. Payload shape mirrors the
 * client-side {@link PtyResizeFrameSchema} so a mirroring consumer's
 * receive path is symmetric with the driver's send path.
 *
 * The learner is the sole winsize authority; a read-only observer (the
 * admin console) consumes this frame to mirror the learner's terminal
 * size but never sends the client-side resize frame upstream.
 *
 * @property type - Literal `'resize'` (discriminant).
 * @property payload - Cols/rows of the guest pty window; positive integers.
 */
export const PtyResizeServerFrameSchema = z.object({
  type: z.literal('resize'),
  payload: z.object({
    cols: z.number().int().positive(),
    rows: z.number().int().positive(),
  }),
})

/** Parsed `PtyResizeServerFrame`. */
export type PtyResizeServerFrame = z.infer<typeof PtyResizeServerFrameSchema>

/**
 * Server → client frames for the pty bridge. Carries the lifecycle
 * frame and the winsize (`resize`) frame; pty bytes flow over the
 * separate binary channel.
 */
export const PtyServerFrameSchema = z.discriminatedUnion('type', [
  PtyLifecycleFrameSchema,
  PtyResizeServerFrameSchema,
])

/** Parsed `PtyServerFrame`. */
export type PtyServerFrame = z.infer<typeof PtyServerFrameSchema>

/**
 * Path params for `PtySub`. `id` names the Machine whose pty the
 * caller is dialing.
 */
export const PtySubParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `PtySubParams`. */
export type PtySubParams = z.infer<typeof PtySubParamsSchema>

/**
 * `WS /hypervisor/:id/pty` — typed subscription for the Machine's
 * pty bridge. Binary frames carry pty bytes; client text frames carry
 * control envelopes (open, resize, resume, suspend). Server text frames
 * carry the lifecycle push and the winsize (`resize`) push.
 */
export const PtySub = defineSubscription({
  path: '/hypervisor/:id/pty',
  params: PtySubParamsSchema,
  clientFrame: PtyControlFrameSchema,
  serverFrame: PtyServerFrameSchema,
  description:
    "Drive a Machine's pty over a typed WS (#832). The first text frame " +
    'MUST be `open` (carrying the boot recipe); the hypervisor dispatches ' +
    'by the dialed vmKey — boot if absent, resume if saved, attach if ' +
    'running. Binary frames are pty bytes; later text frames are typed ' +
    'control envelopes (resize, resume, suspend).',
})

////////////////////////////////////////////////////////////////////////////////
// Gate subscription — control channel events
////////////////////////////////////////////////////////////////////////////////

/**
 * Client → server frames for the gate channel. The gate subscription
 * is read-only from the consumer's perspective (it's a passive
 * command-event observer, per ADR-019); the single `__never` member is
 * the `z.discriminatedUnion` minimum — a union needs ≥1 member and no
 * control verb exists yet. A future control verb replaces `__never`
 * with a real member without a wire-format break.
 */
export const GateClientFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('__never') }),
])

/** Parsed `GateClientFrame`. Forward-reserved; not produced today. */
export type GateClientFrame = z.infer<typeof GateClientFrameSchema>

/**
 * Server → client frames for the gate channel (#1058): the typed
 * per-kind event union from `shared/events.ts`, replacing the old
 * `{kind:string, payload:z.unknown()}` hole. Both wire ends validate
 * against it, so lab's duck-typed `frame.kind` check off raw JSON is
 * gone.
 */
export const GateServerFrameSchema = GateEventFrameSchema

/** Parsed `GateServerFrame`. */
export type GateServerFrame = z.infer<typeof GateServerFrameSchema>

/** Path params for `GateSub`. */
export const GateSubParamsSchema = z.object({
  id: z.string().min(1),
})

/** Parsed `GateSubParams`. */
export type GateSubParams = z.infer<typeof GateSubParamsSchema>

/**
 * `WS /hypervisor/:id/gate` — typed subscription for the Machine's
 * gate channel. Server pushes typed event envelopes; client side is
 * forward-reserved for control verbs.
 */
export const GateSub = defineSubscription({
  path: '/hypervisor/:id/gate',
  params: GateSubParamsSchema,
  clientFrame: GateClientFrameSchema,
  serverFrame: GateServerFrameSchema,
  description:
    "Attach a typed WS to a booted Machine's gate channel — a " +
    'passive command-event observer (ADR-019). Server pushes typed ' +
    'event envelopes (OSC 133 command events).',
})

////////////////////////////////////////////////////////////////////////////////
// Availability subscription — host capacity stream (#1257 Part B)
////////////////////////////////////////////////////////////////////////////////

/** Path params for `AvailabilitySub` — none; it is a single global stream. */
export const AvailabilitySubParamsSchema = z.object({})

/** Parsed `AvailabilitySubParams`. */
export type AvailabilitySubParams = z.infer<typeof AvailabilitySubParamsSchema>

/**
 * Client → server frames for the availability channel. Read-only from the
 * consumer's perspective (a passive host-headroom observer); the single
 * `__never` member is the `z.discriminatedUnion` minimum — no control verb
 * exists yet, and a future one replaces `__never` without a wire-format break.
 */
export const AvailabilityClientFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('__never') }),
])

/** Parsed `AvailabilityClientFrame`. Forward-reserved; not produced today. */
export type AvailabilityClientFrame = z.infer<
  typeof AvailabilityClientFrameSchema
>

/**
 * `WS /hypervisor/availability` — typed subscription streaming host
 * {@link CapacityAvailabilitySchema} (umbrella #1257 Part B, #1281). The server
 * pushes the current snapshot on subscribe and again on every change (admit,
 * reclaim, hold expiry). Lab's queue (Part D) admits its head when a freed slot
 * lands.
 */
export const AvailabilitySub = defineSubscription({
  path: '/hypervisor/availability',
  params: AvailabilitySubParamsSchema,
  clientFrame: AvailabilityClientFrameSchema,
  serverFrame: CapacityAvailabilitySchema,
  description:
    'Stream host capacity availability (umbrella #1257 Part B). Server ' +
    'pushes the current snapshot on subscribe and on every admit / reclaim / ' +
    'hold expiry; the client side is forward-reserved.',
})

////////////////////////////////////////////////////////////////////////////////
// Fleet-lifecycle subscription — cross-fleet transition stream (#1337)
////////////////////////////////////////////////////////////////////////////////

/**
 * Server → client frame on the fleet-lifecycle stream (umbrella #1258
 * Part H / #1337): one push per Machine lifecycle transition across the
 * WHOLE fleet, carrying the transitioning Machine's `id` (the per-machine
 * `PtySub` frame omits it — the `:id` path param scopes that one). The
 * admin control plane subscribes so its Hypervisors panel rows + filters
 * track reality live instead of reconcile-polling. Deliberately leaner
 * than the pty {@link PtyLifecycleFrameSchema}: no budget `usage` window
 * (that's the learner-billing channel, not an operator-observability
 * one) — just the id, the new state, and the transition reason.
 *
 * @property type - Literal `'lifecycle'` (discriminant).
 * @property id - Machine id whose state changed (the fan-out is fleet-wide,
 *   so the id must ride in the payload).
 * @property state - The Machine's new `MachineLifecycleState`.
 * @property reason - Transition trigger (#1056), when the transition has
 *   one — the reclaim/terminal frames carry it, the boot-path frames don't.
 */
export const FleetLifecycleFrameSchema = z.object({
  type: z.literal('lifecycle'),
  id: z.string().min(1),
  state: MachineLifecycleStateSchema,
  reason: MachineLifecycleReasonSchema.optional(),
})

/** Parsed `FleetLifecycleFrame`. */
export type FleetLifecycleFrame = z.infer<typeof FleetLifecycleFrameSchema>

/** Path params for `FleetLifecycleSub` — none; it is a single global stream. */
export const FleetLifecycleSubParamsSchema = z.object({})

/** Parsed `FleetLifecycleSubParams`. */
export type FleetLifecycleSubParams = z.infer<
  typeof FleetLifecycleSubParamsSchema
>

/**
 * Client → server frames for the fleet-lifecycle channel. Read-only from
 * the consumer's perspective (a passive fleet-transition observer); the
 * single `__never` member is the `z.discriminatedUnion` minimum — no
 * control verb exists, and a future one replaces `__never` without a
 * wire-format break.
 */
export const FleetLifecycleClientFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('__never') }),
])

/** Parsed `FleetLifecycleClientFrame`. Forward-reserved; not produced today. */
export type FleetLifecycleClientFrame = z.infer<
  typeof FleetLifecycleClientFrameSchema
>

/**
 * `WS /hypervisor/lifecycle` — typed subscription streaming every
 * Machine's lifecycle transitions across the fleet (umbrella #1258 Part H
 * / #1337). The server pushes a {@link FleetLifecycleFrameSchema} on each
 * transition of any Machine, regardless of pty attachment; the client
 * side is forward-reserved. The admin control plane consumes it to keep
 * the Hypervisors panel fresh without per-machine attach or poll.
 */
export const FleetLifecycleSub = defineSubscription({
  path: '/hypervisor/lifecycle',
  params: FleetLifecycleSubParamsSchema,
  clientFrame: FleetLifecycleClientFrameSchema,
  serverFrame: FleetLifecycleFrameSchema,
  description:
    'Stream every Machine lifecycle transition across the fleet (umbrella ' +
    '#1258 Part H / #1337). Server pushes id + new state + reason on each ' +
    'transition of any Machine; the client side is forward-reserved.',
})
